import { DateTime } from "luxon";
import { Enums } from "./enums";
import { ticketSettingEntity } from "../model/entity/ticket-setting-entity";

/**
 * Closed tickets stay in the Messages sidebar only while they are inside the MSP's reopen window,
 * the same window `ticket/cx/list` applies server-side: today minus `ticketReopenThreshold` days,
 * truncated to midnight UTC. A threshold of 0, a negative one or none at all means 1 day, as on the
 * server.
 *
 * `cx/list` and `tick/cx/list` already leave older closed tickets out, but the SignalR paths refetch
 * a single row through `cx/detail`, which has no such window - so a ticket closed long ago that is
 * touched again (a late comment, a tag edit) would otherwise reappear in the list.
 */
function closedTicketListCutoff(): DateTime {
  const threshold = Number(
    ticketSettingEntity.query().first()?.ticketReopenThreshold ?? 0,
  );
  const days = Number.isFinite(threshold) && threshold > 0 ? threshold : 1;
  return DateTime.utc().startOf("day").minus({ days });
}

/** True when the row is Closed and was closed before the reopen window's cut-off. A Closed row
 * with no readable close date is kept: the server put it there, and nothing here can say otherwise. */
export function isClosedTicketOutsideListWindow(row: {
  ticketStatusId?: number | null;
  ticketClosedOn?: string | null;
}): boolean {
  if (Number(row?.ticketStatusId ?? 0) !== Enums.TicketStatus.Closed) {
    return false;
  }
  const closedOn = DateTime.fromISO(String(row.ticketClosedOn ?? ""), {
    zone: "utc",
  });
  if (!closedOn.isValid) {
    return false;
  }
  return closedOn < closedTicketListCutoff();
}
